import { MinusSVG, PlusSVG } from '@media/svgs'
import Toggle from '../../../../../components/ui/Toggle'
import RippleButton from '../../../../../components/ui/RippleButton'
import NumericInput from '@components/ui/NumericInput'
import { useClassicBetting } from '@hooks/betting/useClassicBetting'
import { TOGGLE, ACTION_VARIANT, BET_MODE } from '@constants/bettingConstants'

const isOn = (control) => control === TOGGLE.ENABLED
// Global class that dims + blocks a locked control for a visible difference.
const lock = (control) => (isOn(control) ? '' : 'disable-actions')

// Sub-label under the bet-style button. START_AUTO gets none (its label already
// says it); PLACED reads as a confirmation because the bet is locked in by then.
const BET_BUTTON_DESC = {
  [ACTION_VARIANT.BET]: 'for the next round',
  [ACTION_VARIANT.PLACED]: 'in this round',
}

// One data-driven action button. BET / START_AUTO / PLACED share the green "bet"
// style (differing by label + sub-label, and PLACED is always inert); CANCEL and
// CASHOUT have their own styles.
const ActionButton = ({ variant, label, enabled, onClick, cashoutAmount }) => {
  const lockCls = enabled ? '' : 'disable-actions'

  if (variant === ACTION_VARIANT.CASHOUT) {
    return (
      <RippleButton
        className={`standard-normal-classic-betting-card-bm-cashout-button-outer-container ${lockCls}`}
        onClick={onClick}
        disabled={!enabled}
      >
        <div className="standard-normal-classic-betting-card-bm-cashout-button-title">{label}</div>
        <div className="standard-normal-classic-betting-card-bm-cashout-button-amount">
          {cashoutAmount}
        </div>
      </RippleButton>
    )
  }

  if (variant === ACTION_VARIANT.CANCEL) {
    return (
      <RippleButton
        className={`standard-normal-classic-betting-card-bm-cancel-button-outer-container ${lockCls}`}
        onClick={onClick}
        disabled={!enabled}
      >
        <div className="standard-normal-classic-betting-card-bm-cancel-button-title">{label}</div>
      </RippleButton>
    )
  }

  // BET, START_AUTO or PLACED
  return (
    <RippleButton
      className={`standard-normal-classic-betting-card-bm-bet-button-outer-container ${lockCls}`}
      onClick={onClick}
      disabled={!enabled}
    >
      <div className="standard-normal-classic-betting-card-bm-bet-button-title">{label}</div>
      {BET_BUTTON_DESC[variant] && (
        <div className="standard-normal-classic-betting-card-bm-bet-button-desc">
          {BET_BUTTON_DESC[variant]}
        </div>
      )}
    </RippleButton>
  )
}

const ClassicBettingCard = ({ panel }) => {
  const {
    stake,
    mode,
    autoCashout,
    liveCashout,
    actionButton,
    interactionControls,
    presetStakes,
    renderValue,
    currencyCode,
    incrementStake,
    decrementStake,
    onStakeInput,
    setQuickStake,
    toggleAutoBet,
    toggleAutoCashout,
    onAutoCashoutInput,
    onAutoCashoutBlur,
    onActionButton,
  } = useClassicBetting(panel)

  const ic = interactionControls

  return (
    <div className="standard-normal-classic-betting-card-outer-container">
      <div className="standard-normal-classic-betting-card-inner-container">
        <div className="standard-normal-classic-betting-card-stake-module-container">
          <RippleButton
            className={`standard-normal-classic-betting-card-stake-control-button ${lock(ic.stakeControlKeys.minusKey)}`}
            onClick={decrementStake}
            disabled={!isOn(ic.stakeControlKeys.minusKey)}
          >
            <MinusSVG />
          </RippleButton>
          <div className="standard-normal-classic-betting-card-stake-input">
            <NumericInput
              className={`standard-normal-classic-betting-card-stake-input-box ${stake ? 'filled' : ''} ${lock(ic.stakeInput)}`}
              value={stake}
              onChange={(event) => onStakeInput(event.target.value)}
              disabled={!isOn(ic.stakeInput)}
            />
            <label className="standard-normal-classic-betting-card-stake-input-label">
              Bet Amount{currencyCode ? ` (${currencyCode})` : ''}
            </label>
          </div>
          <RippleButton
            className={`standard-normal-classic-betting-card-stake-control-button ${lock(ic.stakeControlKeys.plusKey)}`}
            onClick={incrementStake}
            disabled={!isOn(ic.stakeControlKeys.plusKey)}
          >
            <PlusSVG />
          </RippleButton>
        </div>
        <div className="standard-normal-classic-betting-card-quick-stakes-module-container">
          {presetStakes.map((quickStake, index) => (
            <RippleButton
              key={index}
              className={`standard-normal-classic-betting-card-quick-stake-button ${lock(ic.quickStakes)}`}
              onClick={() => setQuickStake(quickStake)}
              disabled={!isOn(ic.quickStakes)}
            >
              {quickStake}
            </RippleButton>
          ))}
        </div>
        <div className="standard-normal-classic-betting-card-button-module-container">
          <ActionButton
            variant={actionButton.variant}
            label={actionButton.label}
            enabled={actionButton.enabled}
            onClick={onActionButton}
            cashoutAmount={liveCashout != null ? renderValue(liveCashout) : ''}
          />
        </div>
        <div className="standard-normal-classic-betting-card-extra-controls-module-container">
          <div className="standard-normal-classic-betting-card-extra-control-box">
            <div className="standard-normal-classic-betting-card-extra-control-box-title">
              Auto Bet
            </div>
            <div className="standard-normal-classic-betting-card-extra-control-box-toggle">
              <Toggle
                isChecked={mode === BET_MODE.AUTO}
                onCheck={toggleAutoBet}
                disabled={!isOn(ic.autoBet)}
                className={lock(ic.autoBet)}
              />
            </div>
          </div>
          <div className="standard-normal-classic-betting-card-extra-control-box">
            <div className="standard-normal-classic-betting-card-extra-control-box-title">
              Auto Cashout
            </div>
            <div className="standard-normal-classic-betting-card-extra-control-box-toggle">
              <Toggle
                isChecked={autoCashout.status === TOGGLE.ENABLED}
                onCheck={toggleAutoCashout}
                disabled={!isOn(ic.autoCashout)}
                className={lock(ic.autoCashout)}
              />
            </div>
            <div
              className={`standard-normal-classic-betting-card-extra-control-box-input ${lock(ic.autoCashout)}`}
            >
              <NumericInput
                compact
                className="standard-normal-classic-betting-card-extra-control-box-input-box"
                value={autoCashout.value ?? ''}
                onChange={(event) => onAutoCashoutInput(event.target.value)}
                onBlur={onAutoCashoutBlur}
                disabled={!isOn(ic.autoCashout) || autoCashout.status !== TOGGLE.ENABLED}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ClassicBettingCard
